import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useOrderDetails } from 'components/context/order_details';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useUserJourneys } from 'components/context/user_journeys';
import PageLoader from 'components/common/page_loader';
import { createShopPaymentWithToken } from 'utilities/shop_payments';
import { getCountryCode, getLocaleString } from 'utilities/locales';
import {
  initCashAppPay,
  cashAppPayParams,
  createPaymentRequest,
  usePaymentSdk,
} from 'utilities/web_payment_sdk';
import getUuidByString from 'uuid-by-string';
import { PAY_WITH_CASH, isFeatureEnabled } from 'utilities/features';
import { pushHistoryWithSearchParams } from 'components/pages/checkout/utilities';
import { FrustrationSignal, UserJourneyName } from 'constants/user_journeys';

// Users are directed here from Cash App after making a Cash App Payment on a mobile device.
//
// If Cash App Payment is valid,
//   pushHistoryWithSearchParams(history, '/review')
// Else
//   pushHistoryWithSearchParams(history, '/information')
//
// Cash App Payment is considered valid when
//   - Feature Flag is on
//   - Order is not installment
//   - tokenResult is present
//   - referenceId matches the order number and payment total

const CashAppRedirect = () => {
  const history = useHistory();
  const scriptLoaded = usePaymentSdk();
  const [orderData, setOrderData] = useState(null);

  const { orderRefresh } = useCheckoutFlow();
  const { number, appID, locationID } = useOrderDetails();
  const { userJourneyTracker } = useUserJourneys();

  const validateReferenceId = (referenceId) => {
    const uuid = getUuidByString(number + orderData.total.amount);
    return uuid === referenceId;
  };

  useEffect(() => {
    if (isFeatureEnabled(PAY_WITH_CASH)) {
      orderRefresh()
        .then((data) => {
          if (!data || data.payment_due.payment_schedule === 'installment') {
            pushHistoryWithSearchParams(history, '/information');
            return;
          }

          setOrderData(data);
        })
        .catch(() => {
          userJourneyTracker.addFrustrationSignal({
            userJourneyName: UserJourneyName.ShopPlaceOrder,
            frustrationSignal:
              FrustrationSignal.CashAppPayStepOrderRefreshError,
          });
          pushHistoryWithSearchParams(history, '/information');
        });
    } else {
      pushHistoryWithSearchParams(history, '/information');
    }
  }, []);

  useEffect(() => {
    if (!orderData || !scriptLoaded) return;

    const payments = window.Square.payments(appID, locationID);
    payments.setLocale(getLocaleString());

    initCashAppPay(
      payments,
      createPaymentRequest(orderData, getCountryCode()),
      // These params don't matter here since we only need the event listener
      cashAppPayParams(window.location.pathname, 'pass'),
    ).then((cashAppPay) => {
      if (!cashAppPay) {
        pushHistoryWithSearchParams(
          history,
          '/information',
          {},
          {
            error: 'cash_app_pay',
          },
        );
        return;
      }

      cashAppPay.addEventListener('ontokenization', (event) => {
        const { tokenResult } = event.detail;
        const referenceId = tokenResult?.details?.cashAppPay?.referenceId;

        if (tokenResult && validateReferenceId(referenceId)) {
          createShopPaymentWithToken(
            number,
            orderData,
            tokenResult,
            payments,
          ).then((response) => {
            return response.ok
              ? pushHistoryWithSearchParams(history, '/review')
              : pushHistoryWithSearchParams(
                  history,
                  '/payment',
                  {},
                  {
                    error: 'cash_app_pay',
                  },
                );
          });
        } else {
          pushHistoryWithSearchParams(
            history,
            '/information',
            {},
            {
              error: 'cash_app_pay',
            },
          );
        }
      });
    });
  }, [orderData, scriptLoaded]);

  return <PageLoader />;
};

export default CashAppRedirect;
